// ============================================================================
// Shared Filter Configuration
// Single source of truth for filter labels, orders, and constants
// Used by: AccountsFilterPanel, ListBuilderFilterPanel, TargetUniverseFilters
// ============================================================================

// ── Account Tier (sales lifecycle) ──────────────────────────────────

export const ACCOUNT_TIER_ORDER = [
  'new',
  'cold_target',
  'warm_target',
  'active_prospect',
  'customer',
  'disqualified',
];

export const ACCOUNT_TIER_LABELS: Record<string, string> = {
  new: 'New (Unreviewed)',
  cold_target: 'Cold Target',
  warm_target: 'Warm Target',
  active_prospect: 'Active Prospect',
  customer: 'Customer',
  disqualified: 'Disqualified',
};

export const INACTIVE_TIERS = ['disqualified'];

// ── ICP Tier ────────────────────────────────────────────────────────

export const ICP_TIER_ORDER = ['Tier 1', 'Tier 2', 'Tier 3', 'Unqualified'];

// ── Sales Rep → Territory Mapping ───────────────────────────────────

export const REP_CONFIG = [
  { name: 'Andrew', territories: ['Massachusetts'] },
  { name: 'Bethanne', territories: ['West'] },
  { name: 'Jeff', territories: ['South / Central', 'LATAM'] },
  { name: 'Nico', territories: ['Northeast / Midwest', 'Canada'] },
  { name: 'Paul', territories: ['EMEA'] },
  { name: 'Kelvin', territories: ['APAC'] },
] as const;

export const REP_TERRITORY_MAP: Record<string, string[]> = {
  Andrew: ['Massachusetts'],
  Bethanne: ['West'],
  Jeff: ['South / Central', 'LATAM'],
  Nico: ['Northeast / Midwest', 'Canada'],
  Paul: ['EMEA'],
  Kelvin: ['APAC'],
};

// ── Clinical Phase ──────────────────────────────────────────────────

export const CLINICAL_PHASE_ORDER = [
  'preclinical',
  'phase_0',
  'phase_1',
  'phase_1_2',
  'phase_2',
  'phase_2_3',
  'phase_3',
  'phase_4',
  'approved',
  'clinical',
];

export const CLINICAL_PHASE_LABELS: Record<string, string> = {
  preclinical: 'Preclinical',
  phase_0: 'Phase 0',
  phase_1: 'Phase 1',
  phase_1_2: 'Phase 1/2',
  phase_2: 'Phase 2',
  phase_2_3: 'Phase 2/3',
  phase_3: 'Phase 3',
  phase_4: 'Phase 4',
  approved: 'Approved',
  clinical: 'Clinical',
};

// ── Funding Status ──────────────────────────────────────────────────

export const FUNDING_LABELS: Record<string, string> = {
  seed: 'Seed',
  pre_seed: 'Pre-Seed',
  series_a: 'Series A',
  series_b: 'Series B',
  series_c: 'Series C',
  series_d_plus: 'Series D+',
  ipo: 'IPO',
  public: 'Public',
  private: 'Private',
  acquired: 'Acquired',
  bootstrapped: 'Bootstrapped',
  non_profit: 'Non-Profit',
  inactive: 'Inactive',
};

// ── Employee Range (Company Size) ───────────────────────────────────

export const EMPLOYEE_RANGE_ORDER = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1001-5000',
  '5000+',
];

// ── Contact: Seniority Levels ───────────────────────────────────────

export const STANDARD_SENIORITY_LEVELS = [
  'C-Suite',
  'VP',
  'Director',
  'Manager',
  'Senior',
  'Mid-Level',
  'Entry-Level',
  'Individual Contributor',
  'Other',
];

// ── Contact: Departments ────────────────────────────────────────────

export const STANDARD_DEPARTMENTS = [
  'Business Development',
  'Clinical / Medical',
  'Engineering',
  'Executive',
  'Finance',
  'HR',
  'IT',
  'Investment',
  'Legal / Regulatory',
  'Manufacturing',
  'Marketing',
  'Operations',
  'Product',
  'Quality',
  'R&D',
  'Sales',
  'Supply Chain',
  'Other',
];

// ── Subcategory Labels ──────────────────────────────────────────────

export const SUBCATEGORY_LABELS: Record<string, string> = {
  td_preclinical: 'TD Preclinical',
  td_clinical: 'TD Clinical',
  td_biopharma: 'TD Biopharma',
  td_platform: 'TD Platform',
  td_commercial: 'TD Commercial',
  td_cell_therapy: 'TD Cell Therapy',
  td_nutraceutical: 'TD Nutraceutical',
  td_digital_health: 'TD Digital Health',
  service_cro: 'CRO',
  service_cdmo: 'CDMO',
  service_cro_cdmo: 'CRO/CDMO',
  ai_university: 'University',
  ai_research_institute: 'Research Institute',
  ai_teaching_hospital: 'Teaching Hospital',
  ai_medical_school: 'Medical School',
  ai_cancer_center: 'Cancer Center',
  ai_childrens_hospital: "Children's Hospital",
  ai_government: 'Government',
  ai_foundation: 'Foundation',
  ai_tech_transfer: 'Tech Transfer',
  non_life_sciences: 'Non-Life Sciences',
  not_therapeutic_developer: 'Not TD',
  not_academic_institution: 'Not Academic',
  fp_foundation: 'Foundation (FP)',
};
